import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { useToast } from '../../components/ds';
import {
  Button, Table, Badge, Spinner, Tabs, DetailHeader,
  SelectPill, Kebab, SplitButton, Icon,
} from '../../components/ds';
import AddCompanyModal from './AddCompanyModal';
import AddApplicationModal from './AddApplicationModal';
import FeaturesModal, { TARGETING_OPTIONS } from './FeaturesModal';
import { ManagePlacementsDrawer } from './ManagePlacementsDrawer';
import './AccountDetailPage.css';

// ─── SelectPill option data ─────────────────────────────────────────────────

const STATUS_META = {
  active:   { label: '啟用',   dot: 'var(--status-success-border)' },
  paused:   { label: '已暫停', dot: 'var(--gray-400)' },
  archived: { label: '已封存', dot: 'var(--gray-300)' },
};

const INT_META = {
  gam:      { label: 'GAM',      dot: 'var(--sp-gam-dot)' },
  hardcode: { label: 'Hardcode', dot: 'var(--sp-hardcode-dot)' },
  '':       { label: '未設定',   dot: 'var(--status-warning-border)' },
};

function getStatusOptions(currentStatus) {
  switch (currentStatus) {
    case 'active':
      return [
        { value: 'active',   label: STATUS_META.active.label,   dot: STATUS_META.active.dot },
        { value: 'paused',   label: '暫停',                     dot: STATUS_META.paused.dot },
        { value: 'archived', label: '封存',                     dot: STATUS_META.archived.dot },
      ];
    case 'paused':
      return [
        { value: 'paused',   label: STATUS_META.paused.label,   dot: STATUS_META.paused.dot },
        { value: 'active',   label: '恢復',                     dot: STATUS_META.active.dot },
        { value: 'archived', label: '封存',                     dot: STATUS_META.archived.dot },
      ];
    case 'archived':
      return [
        { value: 'archived', label: STATUS_META.archived.label, dot: STATUS_META.archived.dot },
        { value: 'paused',   label: '取消封存',                 dot: STATUS_META.paused.dot },
      ];
    default:
      return [{ value: currentStatus, label: currentStatus }];
  }
}

// INT_OPTIONS excludes '' (unset) — backend only accepts 'gam' / 'hardcode'.
// INT_META[''] is kept for display-only rendering of unset apps (SelectPill variant).
const INT_OPTIONS = [
  { value: 'gam',      label: INT_META.gam.label,      dot: INT_META.gam.dot },
  { value: 'hardcode', label: INT_META.hardcode.label,  dot: INT_META.hardcode.dot },
];

// ─── AppRow sub-component ────────────────────────────────────────────────────

function AppRow({ app, companyId, onStatusChange, onIntegrationChange, onManagePlacements, onRefresh, placementCount }) {
  const archived = app.app_status === 'archived';
  const integration = app.integration_type || '';
  const isHard = integration === 'hardcode';

  const statusValue = app.app_status || 'active';
  const statusOptions = getStatusOptions(statusValue);

  return (
    <div className="app-row">
      {/* col 1: name */}
      <div className="app-row__name" title={app.app_name || app.uuid}>
        {app.app_name || app.uuid}
      </div>

      {/* col 2: status SelectPill */}
      <div className="app-cell">
        <SelectPill
          value={statusValue}
          options={statusOptions}
          headLabel="狀態"
          onChange={(newStatus) => onStatusChange(app.uuid, newStatus)}
        />
      </div>

      {/* col 3: integration SelectPill */}
      <div className="app-cell">
        <SelectPill
          value={integration}
          options={INT_OPTIONS}
          headLabel="整合類型"
          disabled={archived}
          onChange={(newInt) => onIntegrationChange(app.uuid, integration, newInt)}
        />
      </div>

      {/* col 4: placements slot — always reserves column width */}
      <div className="app-cell placements-slot">
        {isHard ? (
          <button
            className="mng-link"
            disabled={archived}
            title={archived ? '請先取消封存才能管理版位' : '管理版位'}
            onClick={() => !archived && onManagePlacements(app.uuid, app.app_name || app.uuid, integration, archived)}
          >
            <Icon name="layout-template" size={13} strokeWidth={1.8} />
            管理版位
            {typeof placementCount === 'number' && (
              <span className="mng-cnt">{placementCount}</span>
            )}
          </button>
        ) : (
          <span className="mng-dash" title="僅 Hardcode 類型可自管版位">—</span>
        )}
      </div>

      {/* col 5: kebab */}
      <div className="app-cell">
        <Kebab
          items={[
            {
              label: '從 ADISON 同步',
              icon: 'refresh-cw',
              onClick: () => onRefresh(companyId),
            },
          ]}
        />
      </div>
    </div>
  );
}

// ─── CompanyCard sub-component ───────────────────────────────────────────────

function CompanyCard({
  company,
  accountId,
  defaultOpen,
  onStatusChange,
  onIntegrationChange,
  onManagePlacements,
  onSyncCompany,
  onRemoveCompany,
  onAddApplication,
  syncingCompanyId,
  placementCounts,
  onFetchPlacements,
  placementRefreshKey,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const appCount = (company.applications || []).length;

  const handleToggle = () => setOpen((o) => !o);

  // Fetch placement counts when card is open, and re-fetch after placement changes (placementRefreshKey bump).
  // placementCounts is excluded from deps intentionally — we only want to re-fetch when open state or
  // the refresh key changes, not on every count update (which would cause an infinite loop).
  useEffect(() => {
    if (open) {
      (company.applications || []).forEach((app) => {
        if ((app.integration_type || '') === 'hardcode') {
          onFetchPlacements(app.uuid);
        }
      });
    }
    // eslint-disable-next-line
  }, [open, placementRefreshKey]);

  return (
    <div className={`co-card${open ? ' open' : ''}`}>
      <div className="co-head" onClick={handleToggle} role="button" tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleToggle()}>
        <span className="co-chev">
          <Icon name="chevron-right" size={18} strokeWidth={1.8} />
        </span>
        <div className="co-head__main">
          <div className="co-name">
            <span className="co-name__text">{company.company_name}</span>
            <span className="co-uuid">{company.company_id}</span>
          </div>
          <div className="co-meta">
            {company.tax_id_number && (
              <span>統一編號 <b>{company.tax_id_number}</b></span>
            )}
            {company.address && (
              <span>地址 <b>{company.address}</b></span>
            )}
            <span className="co-meta__apps">
              <span className="co-dot" />
              {appCount} Application{appCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <div className="co-head__right" onClick={(e) => e.stopPropagation()}>
          <Kebab
            items={[
              {
                label: '從 ADISON 廣告後台同步',
                icon: 'refresh-cw',
                onClick: () => onSyncCompany(company.company_id),
              },
              { sep: true },
              {
                label: '移除公司',
                icon: 'trash-2',
                danger: true,
                onClick: () => onRemoveCompany(company.company_id, company.company_name),
              },
            ]}
          />
        </div>
      </div>

      {open && (
        <div className="co-body">
          <div className="app-sub">
            <span className="app-sub__label">Application</span>
            <span className="app-sub__count">· {appCount}</span>
            <span className="spacer" />
            <button
              className="icon-btn sm"
              title="從 ADISON 廣告後台同步"
              disabled={syncingCompanyId === company.company_id}
              onClick={() => onSyncCompany(company.company_id)}
            >
              <Icon
                name="refresh-cw"
                size={15}
                strokeWidth={1.8}
                className={syncingCompanyId === company.company_id ? 'spin' : ''}
              />
            </button>
            <button
              className="add-btn sm"
              onClick={() => onAddApplication(company.company_id)}
            >
              <Icon name="plus" size={14} strokeWidth={1.8} />
              新建 Application
            </button>
          </div>

          {appCount === 0 ? (
            <div className="app-empty">尚無 Application，請同步或新建。</div>
          ) : (
            <div className="app-list">
              {(company.applications || []).map((app) => (
                <AppRow
                  key={app.uuid}
                  accountId={accountId}
                  app={app}
                  companyId={company.company_id}
                  onStatusChange={onStatusChange}
                  onIntegrationChange={onIntegrationChange}
                  onManagePlacements={onManagePlacements}
                  onRefresh={onSyncCompany}
                  placementCount={placementCounts[app.uuid]}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function AccountDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [account, setAccount] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [addCompanyBranch, setAddCompanyBranch] = useState('join'); // 'join' | 'create'
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [features, setFeatures] = useState(null);
  const [disabling, setDisabling] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingCompanyId, setSyncingCompanyId] = useState(null);
  const [showAddApplication, setShowAddApplication] = useState(null); // companyId or null
  const [activeTab, setActiveTab] = useState('users');

  // drawer target for ManagePlacementsDrawer (AS-191)
  const [drawerTarget, setDrawerTarget] = useState(null); // { appId, appName, integration, archived }

  // placement counts per app uuid (loaded lazily when card expands)
  const [placementCounts, setPlacementCounts] = useState({});
  const fetchingPlacements = useRef(new Set());
  // Bump to force already-open cards to re-fetch placement counts after drawer changes
  const [placementRefreshKey, setPlacementRefreshKey] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [accounts, usersData, companiesData, featuresData, applicationsData] = await Promise.all([
        api.getAccounts(),
        api.getUsers(id),
        api.getAccountCompanies(id),
        api.getAccountFeatures(id),
        api.getAccountApplications(id),
      ]);

      const acct = (accounts || []).find((a) => a.uuid === id);
      setAccount(acct || null);
      setUsers(usersData || []);
      setFeatures(featuresData?.features || null);

      // Build integration type map from DB mirror keyed by app_uuid
      const integrationMap = {};
      (applicationsData?.applications || []).forEach((app) => {
        integrationMap[app.app_uuid] = app.integration_type || '';
      });

      // Build status map from DB mirror
      const statusMap = {};
      (applicationsData?.applications || []).forEach((app) => {
        statusMap[app.app_uuid] = app.status || 'active';
      });

      // Enrich companies with detail + applications (merged with integration/status from DB mirror)
      const enriched = await Promise.all(
        (companiesData || []).map(async (c) => {
          try {
            const [detail, apps] = await Promise.all([
              api.getCompanyDetail(c.company_id),
              api.getApplications(c.company_id),
            ]);
            // Merge PAC app data with DB mirror data
            const enrichedApps = (apps || []).map((app) => ({
              ...app,
              // Prefer DB mirror status (app_status from PAC) — use PAC's own status field
              app_status: app.app_status || statusMap[app.uuid] || 'active',
              integration_type: integrationMap[app.uuid] !== undefined
                ? integrationMap[app.uuid]
                : (app.integration_type || ''),
            }));
            return {
              ...c,
              tax_id_number: detail?.tax_id_number || '',
              address: detail?.address || '',
              applications: enrichedApps,
            };
          } catch {
            return { ...c, tax_id_number: '', address: '', applications: [] };
          }
        })
      );
      setCompanies(enriched);
    } catch (err) {
      addToast('載入帳號詳情失敗：' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [id, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Lazily fetch placement count for a hardcode app
  const handleFetchPlacements = useCallback(async (appUuid) => {
    if (fetchingPlacements.current.has(appUuid)) return;
    fetchingPlacements.current.add(appUuid);
    try {
      const data = await api.getApplicationPlacements(appUuid);
      const count = (data?.placements || data || []).length;
      setPlacementCounts((prev) => ({ ...prev, [appUuid]: count }));
    } catch {
      setPlacementCounts((prev) => ({ ...prev, [appUuid]: 0 }));
    }
  }, []);

  // Refresh placement count for a specific app after drawer changes
  const refreshApplications = useCallback(async () => {
    await fetchData();
    // Clear cached counts and in-flight guards so already-open cards will re-fetch via placementRefreshKey
    setPlacementCounts({});
    fetchingPlacements.current.clear();
    setPlacementRefreshKey((k) => k + 1);
  }, [fetchData]);

  // ── Handlers ──

  const handleSyncApplications = async () => {
    setSyncing(true);
    try {
      const result = await api.syncAccountApplications(id);
      const errMsg = result.errors && result.errors.length ? `，${result.errors.length} 個錯誤` : '';
      addToast(`同步完成，${result.synced} 個 Application${errMsg}`, 'success');
      fetchData();
    } catch (err) {
      addToast('同步失敗：' + err.message, 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncCompanyApplications = async (companyId) => {
    setSyncingCompanyId(companyId);
    try {
      const result = await api.syncAccountApplications(id);
      const errMsg = result.errors && result.errors.length ? `，${result.errors.length} 個錯誤` : '';
      addToast(`同步完成，${result.synced} 個 Application${errMsg}`, 'success');
      fetchData();
    } catch (err) {
      addToast('同步失敗：' + err.message, 'error');
    } finally {
      setSyncingCompanyId(null);
    }
  };

  const handleChangeAppStatus = async (appUuid, newStatus) => {
    try {
      await api.changeApplicationStatus(appUuid, newStatus);
      const labelMap = { active: '已啟用', paused: '已暫停', archived: '已封存' };
      addToast(`Application ${labelMap[newStatus] || newStatus}`, 'success');
      fetchData();
    } catch (err) {
      addToast('狀態變更失敗：' + err.message, 'error');
    }
  };

  const handleChangeIntegrationType = async (appUuid, oldType, newType) => {
    if (newType === oldType) return;
    try {
      await api.updateApplicationIntegrationType(id, appUuid, newType);
      addToast('整合類型已更新', 'success');
      // Clear cached placement count when switching away from hardcode
      if (oldType === 'hardcode' && newType !== 'hardcode') {
        setPlacementCounts((prev) => {
          const next = { ...prev };
          delete next[appUuid];
          return next;
        });
        fetchingPlacements.current.delete(appUuid);
      }
      fetchData();
    } catch (err) {
      addToast('更新失敗：' + err.message, 'error');
    }
  };

  const handleManagePlacements = (appUuid, appName, integration, archived) => {
    setDrawerTarget({ appId: appUuid, appName, integration, archived });
  };

  const handleDisable = async () => {
    if (!window.confirm('確定要停用嗎？')) return;
    setDisabling(true);
    try {
      await api.disableAccount(id);
      addToast('帳號已停用', 'success');
      fetchData();
    } catch (err) {
      addToast('停用帳號失敗：' + err.message, 'error');
    } finally {
      setDisabling(false);
    }
  };

  const handleRemoveCompany = async (companyId, companyName) => {
    if (!window.confirm(`移除「${companyName}」？此操作會同時移除 ADISON 廣告後台委託關係。`)) return;
    try {
      await api.removeAccountCompany(id, companyId);
      addToast('已移除公司', 'success');
      fetchData();
    } catch (err) {
      addToast('移除公司失敗：' + err.message, 'error');
    }
  };

  const handleCompanyAdded = () => {
    setShowAddCompany(false);
    addToast('已新增公司', 'success');
    fetchData();
  };

  const handleApplicationAdded = () => {
    setShowAddApplication(null);
    addToast('已建立 Application', 'success');
    fetchData();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const formatTimezone = (tz) => {
    if (!tz) return '-';
    try {
      const parsed = JSON.parse(tz);
      return parsed.name || tz;
    } catch {
      return tz;
    }
  };

  // Derived counts for section bar
  const totalApps = companies.reduce(
    (sum, c) => sum + (c.applications || []).length, 0
  );

  if (loading) {
    return <div className="loading"><Spinner /></div>;
  }

  if (!account) {
    return <div className="loading">找不到帳號</div>;
  }

  return (
    <div>
      <DetailHeader
        navigation={
          <Button variant="secondary" onClick={() => navigate('/accounts')}>
            ← 返回帳號列表
          </Button>
        }
        title={account.account_name}
        status={
          <Badge variant={account.account_status === 'active' ? 'success' : 'default'}>
            {account.account_status}
          </Badge>
        }
        subtitle={`時區：${formatTimezone(account.timezone)}`}
        actions={
          account.account_status === 'active' ? (
            <Button
              variant="secondary"
              style={{ color: 'var(--status-error-text)' }}
              onClick={handleDisable}
              disabled={disabling}
            >
              {disabling ? '停用中...' : '停用帳號'}
            </Button>
          ) : null
        }
      />

      {/* Tab Navigation */}
      <Tabs
        items={[
          { key: 'users', label: '使用者' },
          { key: 'features', label: '功能設定' },
          { key: 'companies', label: '公司' },
        ]}
        activeKey={activeTab}
        onChange={setActiveTab}
      />

      {/* ── Users ── */}
      {activeTab === 'users' && (
        <div className="detail-section" style={{ marginBottom: 32 }}>
          <h2 style={{ marginBottom: 16 }}>使用者</h2>
          <Table
            columns={[
              { key: 'user_name', title: '使用者名稱' },
              { key: 'email', title: 'Email' },
              { key: 'role', title: '角色', render: (_, row) => (row.account_user_attribute?.role_ids || row.role_ids || []).join(', ') },
              { key: 'CreatedAt', title: '建立日期', render: (val) => formatDate(val) },
            ]}
            data={users}
            rowKey="uuid"
            emptyText="無使用者"
          />
        </div>
      )}

      {/* ── Feature Settings ── */}
      {activeTab === 'features' && (
        <div className="detail-section" style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2>功能設定</h2>
            <Button variant="secondary" onClick={() => setShowFeaturesModal(true)}>編輯</Button>
          </div>
          <div style={{ padding: 20, background: 'var(--bg-primary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
            <h3 style={{ marginBottom: 12, fontSize: 14, color: 'var(--text-secondary)' }}>
              Targeting
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {TARGETING_OPTIONS.map((opt) => (
                <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={features?.targeting?.[opt.key] || false}
                    disabled
                    style={{ width: 18, height: 18 }}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Companies ── */}
      {activeTab === 'companies' && (
        <div className="detail-section companies-tab" style={{ marginBottom: 32 }}>

          {/* Section bar */}
          <div className="sec-bar">
            <span className="sec-bar__title">公司</span>
            <span className="count-tag">
              {companies.length} 間 · {totalApps} Application{totalApps !== 1 ? 's' : ''}
            </span>
            <span className="spacer" />
            <button
              className={`icon-btn${syncing ? ' spin-wrap' : ''}`}
              title="從 ADISON 廣告後台同步"
              disabled={syncing}
              onClick={handleSyncApplications}
            >
              <Icon
                name="refresh-cw"
                size={16}
                strokeWidth={1.8}
                className={syncing ? 'spin' : ''}
              />
            </button>
            <SplitButton
              label="+ 新建 Company"
              icon="plus"
              onMain={() => { setAddCompanyBranch('create'); setShowAddCompany(true); }}
              menu={[
                {
                  label: '新建 Company',
                  icon: 'plus',
                  onClick: () => { setAddCompanyBranch('create'); setShowAddCompany(true); },
                },
                {
                  label: '加入既有 Company',
                  icon: 'link',
                  onClick: () => { setAddCompanyBranch('join'); setShowAddCompany(true); },
                },
              ]}
            />
          </div>

          {/* Company card stack */}
          {companies.length === 0 ? (
            <div className="companies-empty">
              尚無關聯公司
            </div>
          ) : (
            <div className="co-stack">
              {companies.map((company, i) => (
                <CompanyCard
                  key={company.company_id}
                  company={company}
                  accountId={id}
                  defaultOpen={i === 0}
                  onStatusChange={handleChangeAppStatus}
                  onIntegrationChange={handleChangeIntegrationType}
                  onManagePlacements={handleManagePlacements}
                  onSyncCompany={handleSyncCompanyApplications}
                  onRemoveCompany={handleRemoveCompany}
                  onAddApplication={setShowAddApplication}
                  syncingCompanyId={syncingCompanyId}
                  placementCounts={placementCounts}
                  onFetchPlacements={handleFetchPlacements}
                  placementRefreshKey={placementRefreshKey}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Modals ── */}

      {showAddCompany && (
        <AddCompanyModal
          accountId={id}
          initialBranch={addCompanyBranch}
          onClose={() => setShowAddCompany(false)}
          onAdded={handleCompanyAdded}
        />
      )}

      {showAddApplication && (
        <AddApplicationModal
          accountId={id}
          companyId={showAddApplication}
          onClose={() => setShowAddApplication(null)}
          onAdded={handleApplicationAdded}
        />
      )}

      {showFeaturesModal && (
        <FeaturesModal
          accountUuid={id}
          features={features}
          onClose={() => setShowFeaturesModal(false)}
          onSaved={(updatedFeatures) => {
            setFeatures(updatedFeatures);
            setShowFeaturesModal(false);
            addToast('功能設定已更新', 'success');
          }}
        />
      )}

      <ManagePlacementsDrawer
        open={!!drawerTarget}
        onClose={() => setDrawerTarget(null)}
        accountId={id}
        appId={drawerTarget?.appId}
        appName={drawerTarget?.appName}
        integration={drawerTarget?.integration}
        archived={drawerTarget?.archived}
        onChanged={refreshApplications}
      />
    </div>
  );
}
